import Link from '~/components/Link'
import type { InAppNotificationResponse, NotificationLinkSegment } from '~/data/api/client/workflows'
import resolveEntityRoute from '~/navigation/entityRoutes'

type NotificationBodyProps = {
  body: string
  /** `null` arrives on the wire for a link-free body even though the spec types the field as optional only. */
  segments?: InAppNotificationResponse['bodySegments'] | null
  /** Called when the reader follows a link, e.g. to close the surrounding overlay. */
  onNavigate?: () => void
}

/**
 * Renders a notification's message. `body` is the canonical plain text and is
 * shown as-is whenever the notification carries no segments; when it does,
 * the segments hold the same text with each entity reference resolved to that
 * entity's detail page at display time.
 */
function NotificationBody({ body, segments, onNavigate }: NotificationBodyProps) {
  if (!segments?.length) return <>{body}</>

  return (
    <>
      {segments.map((segment, index) =>
        segment.type === 'text' ? (
          // Segments are a fixed, ordered snapshot of one message; position is their identity.
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{segment.text}</span>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <SegmentLink key={index} segment={segment} onNavigate={onNavigate} />
        )
      )}
    </>
  )
}

type SegmentLinkProps = {
  segment: NotificationLinkSegment
  onNavigate?: () => void
}

/** A kind with no detail page degrades to its label as plain text rather than a dead link. */
function SegmentLink({ segment, onNavigate }: SegmentLinkProps) {
  const href = resolveEntityRoute(segment.entity, segment.entityId)
  if (!href) return <span>{segment.label}</span>

  return (
    <Link href={href} onClick={onNavigate}>
      {segment.label}
    </Link>
  )
}

export default NotificationBody
